import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { User } from 'src/app/models/user.model';
import { UsersService } from 'src/app/services/apis/users.service';
import {
  LoadUsers,
  LoadUsersSuccess,
  LoadUsersFailure,
  RegisterUser,
  RegisterUserSuccessAction,
  RegisterUserFailureAction,
  SetPasswordAction,
  SetPasswordSuccessAction,
  SetPasswordFailureAction,
  ConfirmEmailAction,
  ConfirmEmailSuccessAction,
  ConfirmEmailFailureAction,
  UpdateUserAction,
  UpdateUserSuccessAction,
  UpdateUserFailureAction,
  ResetPasswordAction,
  ResetPasswordSuccessAction,
  ResetPasswordFailureAction,
} from '../actions/users.actions';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';

export interface UserStateModel {
  items: User[];
  loaded: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  pageNumber: number;
  totalCount: number;
  totalPages: number;
}

const initialState: UserStateModel = {
  items: [],
  loaded: false,
  hasNextPage: false,
  hasPreviousPage: false,
  pageNumber: 0,
  totalCount: 0,
  totalPages: 0,
};

@State<UserStateModel>({
  name: 'users',
  defaults: initialState,
})
@Injectable()
export class UsersState {
  constructor(private service: UsersService) {}

  @Selector()
  static getUsers(state: UserStateModel): User[] {
    return state.items;
  }

  @Selector()
  static getUserById(state: UserStateModel) {
    return (id: string) => state.items.find((user) => user.id === id);
  }

  @Selector()
  static isLoaded(state: UserStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getCount(state: UserStateModel): number {
    return state.totalCount;
  }

  @Selector()
  static paginationInfo(state: UserStateModel): Omit<UserStateModel, 'items'> {
    return {
      loaded: state.loaded,
      hasNextPage: state.hasNextPage,
      hasPreviousPage: state.hasPreviousPage,
      pageNumber: state.pageNumber,
      totalCount: state.totalCount,
      totalPages: state.totalPages,
    };
  }

  @Action(LoadUsers)
  loadUsers(ctx: StateContext<UserStateModel>, action: LoadUsers) {
    const { page, pageSize, sort, sortDirection, filters } = action;

    return this.service
      .load({ page, pageSize, sort, sortDirection, filters })
      .pipe(
        tap((result) => {
          ctx.dispatch(new LoadUsersSuccess(result));
        }),
        catchError((error) => {
          ctx.dispatch(new LoadUsersFailure(error));
          return throwError(() => error);
        }),
      );
  }

  @Action(LoadUsersSuccess)
  loadUsersSuccess(
    ctx: StateContext<UserStateModel>,
    action: LoadUsersSuccess,
  ) {
    const { list } = action;
    if (list) {
      ctx.patchState({
        items: list.items,
        loaded: true,
        hasNextPage: list.hasNextPage,
        hasPreviousPage: list.hasPreviousPage,
        pageNumber: list.pageNumber,
        totalCount: list.totalCount,
        totalPages: list.totalPages,
      });
    }
  }

  @Action(RegisterUser)
  registerUser(ctx: StateContext<UserStateModel>, action: RegisterUser) {
    return this.service.register(action.register).pipe(
      tap(() => {
        ctx.dispatch(new RegisterUserSuccessAction());
      }),
      catchError((error) => {
        ctx.dispatch(new RegisterUserFailureAction(error));
        return throwError(() => error);
      }),
    );
  }

  @Action(SetPasswordAction)
  setPassword(ctx: StateContext<UserStateModel>, action: SetPasswordAction) {
    return this.service.setPassword(action.data).pipe(
      tap(() => {
        ctx.dispatch(new SetPasswordSuccessAction());
      }),
      catchError((error) => {
        ctx.dispatch(new SetPasswordFailureAction(error));
        return throwError(() => error);
      }),
    );
  }

  @Action(ResetPasswordAction)
  resetPassword(
    ctx: StateContext<UserStateModel>,
    action: ResetPasswordAction,
  ) {
    return this.service.resetPassword(action.data).pipe(
      tap(() => {
        ctx.dispatch(new ResetPasswordSuccessAction());
      }),
      catchError((error) => {
        ctx.dispatch(new ResetPasswordFailureAction(error));
        return throwError(() => error);
      }),
    );
  }

  @Action(ConfirmEmailAction)
  confirmEmail(ctx: StateContext<UserStateModel>, action: ConfirmEmailAction) {
    return this.service.confirmEmail(action.data.email).pipe(
      tap(() => {
        ctx.dispatch(new ConfirmEmailSuccessAction());
      }),
      catchError((error) => {
        ctx.dispatch(new ConfirmEmailFailureAction(error));
        return throwError(() => error);
      }),
    );
  }

  @Action(UpdateUserAction)
  updateUser(ctx: StateContext<UserStateModel>, action: UpdateUserAction) {
    return this.service.update(action.update).pipe(
      switchMap(() =>
        this.service.load({
          page: 1,
          pageSize: 1,
          sort: 'id',
          sortDirection: 'asc',
          filters: [`id.Equals.${action.update.id}`],
        }),
      ),
      tap((response) => {
        const updatedUserFromResponse = response.items[0];
        const state = ctx.getState();
        const updatedItems = state.items.map((user) =>
          user.id === updatedUserFromResponse.id
            ? updatedUserFromResponse
            : user,
        );
        ctx.patchState({
          items: updatedItems,
        });
        ctx.dispatch(new UpdateUserSuccessAction());
      }),
      catchError((error) => {
        ctx.dispatch(new UpdateUserFailureAction(error));
        return throwError(() => error);
      }),
    );
  }
}
